import { BiodiversityIcon } from "@/components/icons/chapters/MetricGroupIcons";
import EnvironmentalFootprintIcon from "@/components/icons/EnvironmentalFootprintIcon";
import WaterUseIcon from "@/components/icons/WaterUseIcon";
import {
  formatEnvironmentalCost,
  formatMassWithUnit,
  formatScientific,
} from "@/pages/dashboard/utils/formatters";
import { EFImpactMetricKey, ImpactMetric } from "@/pages/dashboard/utils/types";

const EF_MAIN_METRICS: Record<
  "ef_environmental_cost" | "ef_land_use" | "ef_water_use",
  ImpactMetric
> = {
  ef_environmental_cost: {
    formatter: formatEnvironmentalCost,
    label: "Environmental footprint",
    key: "ef_environmental_cost",
    icon: <EnvironmentalFootprintIcon />,
    unit: "EUR",
    unitTooltip:
      "If Planet Earth had a bank account, they'd invoice you for this amount for using its resources. This is calculated by assigning monetary values(€) to all the individual environmental impact categories, i.e. climate change, ecosystem quality, material resources, pollution, toxicity and water use.",
    impactGroup: "Main impacts",
  },
  ef_land_use: {
    formatter: formatScientific("dimensionless"),
    label: "Land use (agricultural)",
    key: "ef_land_use",
    icon: <BiodiversityIcon />,
    unit: "dimensionless",
    unitTooltip:
      "The impact of occupying or transforming land for activities like agriculture, forestry, mining, or infrastructure. Reflects how land use affects ecosystems, soil quality, and biodiversity over time. ",
    impactGroup: "Main impacts",
  },
  ef_water_use: {
    formatter: formatScientific("m3 world Eq deprived"),
    label: "Water use",
    key: "ef_water_use",
    icon: <WaterUseIcon />,
    unit: "m3 world Eq deprived",
    unitTooltip:
      "The impact of freshwater consumption across the product life cycle, including water used in farming, manufacturing, and processing. Focuses on how much water is withdrawn and not returned to the local ecosystem. ",
    impactGroup: "Main impacts",
  },
};

const EF_CARBON_METRICS: Record<
  | "ef_climate_change_biogenic"
  | "ef_climate_change_fossil"
  | "ef_climate_change_land_use_and_land_use_change",
  ImpactMetric
> = {
  ef_climate_change_biogenic: {
    formatter: formatMassWithUnit("CO2-Eq"),
    label: "Carbon footprint (biogenic)",
    key: "ef_climate_change_biogenic",
    unit: "CO2-Eq",
    unitTooltip:
      "Greenhouse gas emissions and removals from biological sources like plants, trees, and agricultural biomass. Includes CO₂ absorbed during growth and released during decomposition or combustion.",
    impactGroup: "Carbon footprint",
  },
  ef_climate_change_fossil: {
    formatter: formatMassWithUnit("CO2-Eq"),
    label: "Carbon footprint (fossil)",
    key: "ef_climate_change_fossil",
    unit: "CO2-Eq",
    unitTooltip:
      "Greenhouse gas emissions from fossil fuel use across the product life cycle. Includes emissions from energy generation, transportation, and industrial processes.",
    impactGroup: "Carbon footprint",
  },
  ef_climate_change_land_use_and_land_use_change: {
    formatter: formatMassWithUnit("CO2-Eq"),
    label: "Carbon footprint (FLAG)",
    key: "ef_climate_change_land_use_and_land_use_change",
    unit: "CO2-Eq",
    unitTooltip:
      "Greenhouse gas emissions from land-based activities like forestry, agriculture, and land use change (FLAG). Includes emissions from deforestation, soil disturbance, and livestock. ",
    impactGroup: "Carbon footprint",
  },
};

const EF_BIODIVERSITY_METRICS: Record<
  | "ef_ecotoxicity_freshwater"
  | "ef_ecotoxicity_freshwater_inorganics"
  | "ef_ecotoxicity_freshwater_organics"
  | "ef_eutrophication_freshwater"
  | "ef_eutrophication_marine"
  | "ef_eutrophication_terrestrial"
  | "ef_acidification",
  ImpactMetric
> = {
  ef_ecotoxicity_freshwater: {
    formatter: formatScientific("CTUe"),

    label: "Freshwater toxicity",
    key: "ef_ecotoxicity_freshwater",
    unit: "CTUe",
    unitTooltip:
      "The impact on freshwater species from exposure to toxic substances, such as heavy metals and pesticides, released into rivers, lakes, and wetlands. ",
    impactGroup: "Biodiversity",
  },
  ef_ecotoxicity_freshwater_inorganics: {
    formatter: formatScientific("CTUe"),

    label: "Marine toxicity",
    key: "ef_ecotoxicity_freshwater_inorganics",
    unit: "CTUe",
    unitTooltip:
      "The impact on marine biodiversity from toxic pollutants entering oceans and seas, including persistent organic pollutants, oil spills, and industrial discharges. ",
    impactGroup: "Biodiversity",
  },
  ef_ecotoxicity_freshwater_organics: {
    formatter: formatScientific("CTUe"),

    label: "Land toxicity",
    key: "ef_ecotoxicity_freshwater_organics",
    unit: "CTUe",
    unitTooltip:
      "The impact on land-based species from toxic chemicals accumulating in soil, such as heavy metals, pesticides, and industrial waste. ",
    impactGroup: "Biodiversity",
  },
  ef_eutrophication_freshwater: {
    formatter: formatMassWithUnit("P-Eq"),

    label: "Freshwater pollution",
    key: "ef_eutrophication_freshwater",
    unit: "P-Eq",
    unitTooltip:
      "Biodiversity loss in freshwater ecosystems caused by general pollution, including nutrient runoff, organic waste, and industrial discharges. ",
    impactGroup: "Biodiversity",
  },
  ef_eutrophication_marine: {
    formatter: formatMassWithUnit("N-Eq"),

    label: "Marine pollution",
    key: "ef_eutrophication_marine",
    unit: "N-Eq",
    unitTooltip:
      "Biodiversity loss in marine ecosystems caused by pollution from plastic waste, oil, nutrients, and chemicals affecting marine life and habitats. ",
    impactGroup: "Biodiversity",
  },
  ef_eutrophication_terrestrial: {
    formatter: formatScientific("mol N-Eq"),
    label: "Land pollution (nutrients)",
    key: "ef_eutrophication_terrestrial",
    unit: "mol N-Eq",
    unitTooltip:
      "The impact on land biodiversity from excessive nutrient inputs, such as nitrogen and phosphorus, which disrupt soil ecosystems and natural vegetation. ",
    impactGroup: "Biodiversity",
  },
  ef_acidification: {
    formatter: formatScientific("mol H+-Eq"),
    label: "Land pollution (acids)",
    key: "ef_acidification",
    unit: "mol H+-Eq",
    unitTooltip:
      "The impact on land ecosystems from acidifying emissions, such as sulphur dioxide and nitrogen oxides, which lead to soil acidification and damage to plant and microbial life. ",
    impactGroup: "Biodiversity",
  },
};

const EF_HUMAN_HEALTH_METRICS: Record<
  | "ef_human_toxicity_carcinogenic"
  | "ef_human_toxicity_non_carcinogenic"
  | "ef_ionising_radiation_human_health"
  | "ef_ozone_depletion"
  | "ef_particulate_matter_formation"
  | "ef_photochemical_oxidant_formation_human_health",
  ImpactMetric
> = {
  ef_human_toxicity_carcinogenic: {
    formatter: formatScientific("CTUh"),

    label: "Human toxicity (carcinogenic)",
    key: "ef_human_toxicity_carcinogenic",
    unit: "CTUh",
    unitTooltip:
      "Potential human health impacts from exposure to substances that can cause cancer. Includes emissions of carcinogenic chemicals like certain heavy metals, solvents, and combustion byproducts.",
    impactGroup: "Human health",
  },
  ef_human_toxicity_non_carcinogenic: {
    formatter: formatScientific("CTUh"),

    label: "Human toxicity (non-carcinogenic)",
    key: "ef_human_toxicity_non_carcinogenic",
    unit: "CTUh",
    unitTooltip:
      "Potential human health impacts from substances that do not cause cancer but may lead to chronic or acute health effects. Includes exposure to pollutants like ammonia, fluoride, and non-carcinogenic heavy metals.",
    impactGroup: "Human health",
  },
  ef_ionising_radiation_human_health: {
    formatter: formatScientific("kBq U235-Eq"),

    label: "Radiation exposure",
    key: "ef_ionising_radiation_human_health",
    unit: "kBq U235-Eq",
    unitTooltip:
      "The impact on human health from exposure to ionising radiation during the product life cycle, such as emissions from energy production or material processing. Measured in disability-adjusted life years (DALYs).",
    impactGroup: "Human health",
  },
  ef_ozone_depletion: {
    formatter: formatMassWithUnit("CFC-11-Eq"),
    label: "Ozone depletion",
    key: "ef_ozone_depletion",
    unit: "CFC-11-Eq",
    unitTooltip:
      "The impact on human health caused by the thinning of the ozone layer, which increases exposure to harmful UV radiation. Driven by emissions of substances like CFCs and halons. ",
    impactGroup: "Human health",
  },
  ef_particulate_matter_formation: {
    formatter: formatScientific("disease incidence"),

    label: "Air pollution",
    key: "ef_particulate_matter_formation",
    unit: "disease incidence",
    unitTooltip:
      "The impact on human health from inhaling fine particulate matter (PM2.5), nitrogen oxides, and other air pollutants released during production, transport, or energy use. ",
    impactGroup: "Human health",
  },
  ef_photochemical_oxidant_formation_human_health: {
    formatter: formatMassWithUnit("NMVOC-Eq"),

    label: "Smog (human health)",
    key: "ef_photochemical_oxidant_formation_human_health",
    unit: "NMVOC-Eq",
    unitTooltip:
      "The impact on human health from exposure to ground-level ozone formed by chemical reactions between sunlight and pollutants like VOCs and NOₓ. ",
    impactGroup: "Human health",
  },
};

const EF_RESOURCE_SCARCITY_METRICS: Record<
  "ef_energy_resources_non_renewable" | "ef_material_resources_metals_minerals",
  ImpactMetric
> = {
  ef_energy_resources_non_renewable: {
    formatter: formatScientific("MJ, net calorific value"),
    label: "Fossil fuel",
    key: "ef_energy_resources_non_renewable",
    unit: "MJ, net calorific value",
    unitTooltip:
      "The impact of extracting and consuming fossil energy sources like coal, oil, and natural gas. Reflects both depletion and associated environmental pressure. ",
    impactGroup: "Resource scarcity",
  },
  ef_material_resources_metals_minerals: {
    formatter: formatMassWithUnit("Sb-Eq"),
    label: "Mineral resources",
    key: "ef_material_resources_metals_minerals",
    unit: "Sb-Eq",
    unitTooltip:
      "The impact of extracting and depleting non-renewable mineral resources such as metals and rare earth elements. Reflects long-term availability risks. ",
    impactGroup: "Resource scarcity",
  },
};

export const ALL_EF_IMPACTS: Record<EFImpactMetricKey, ImpactMetric> = {
  ...EF_MAIN_METRICS,
  ...EF_CARBON_METRICS,
  ...EF_BIODIVERSITY_METRICS,
  ...EF_HUMAN_HEALTH_METRICS,
  ...EF_RESOURCE_SCARCITY_METRICS,
};
